// src/v2/hooks/useDTRWithOT.js
import { useState, useCallback, useEffect } from "react";
import overtimeService from "../services/overtimeService";
import dtrWithOtService from "../services/dtrWithOtService"; // New service for Upsert/Approve
import { SetAuditTrail } from "../../components/Reports/AuditTrailReportEmployeeSetup";

export const useDTRWithOT = (showGeneralMessage, apiFilterPayload, additionalFilters, user) => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [filterData, setFilterData] = useState([]);

  const fetchLogs = useCallback(async () => {
    if (!additionalFilters?.dateFrom || !additionalFilters?.dateTo) return;

    setLoading(true);
    try {
      const res = await overtimeService.getDtrLogs(apiFilterPayload, additionalFilters);
      const fetchedData = res.data || [];
      setLogs(fetchedData);
      setFilterData(fetchedData);
    } catch (err) {
      console.error("DTR Fetch Error:", err);
      const msg = err.response?.data?.message || "Error loading logs";
      showGeneralMessage(msg, false);
    } finally {
      setLoading(false);
    }
  }, [showGeneralMessage, apiFilterPayload, additionalFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  /**
   * Action Handler Wrapper
   * Standardizes Save -> Audit -> UI Feedback -> Refresh
   */
  const handleAction = async (actionFn, auditData, successMsg, ...args) => {
    setLoading(true);
    try {
      const res = await actionFn(...args);

      if (auditData) {
        await SetAuditTrail(auditData);
      }

      await fetchLogs(); // Refresh the table
      showGeneralMessage(res.data?.message || successMsg, true);
      return { success: true, data: res.data };
    } catch (err) {
      const errorMsg = err.response?.data?.message || "An error occurred";
      showGeneralMessage(errorMsg, false);
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  /**
   * Upsert OT Override
   */
  const upsertOTOverride = async (form, originalOTValue) => {
    const audit = {
      setupArea: "DTR With OT Viewing",
      actionTaken: "update",
      field: "Overtime Value Override",
      newValue: `Value: ${form.overtimeValue}`,
      oldValue: `Calculated: ${originalOTValue}`,
      dateSaved: new Date(),
      savedById: user.id
    };

    return await handleAction(
      dtrWithOtService.upsert,
      audit,
      "Overtime updated successfully",
      form
    );
  };


  const approveOT = async (item, userId) => {
    const payload = {
      employeePersonalInformationId: item.id,
      timeEntryDate: item.date,
      overtimeValue: item.otHours,
      isRestDay: item.isRestDay,
      approverId: userId
    };

    const audit = {
      setupArea: "DTR With OT Viewing",
      actionTaken: "approve",
      field: "Approval",
      newValue: `Approved OT: ${item.otHours} for ${item.employeePersonalInformationFullName}`,
      oldValue: "Pending",
      dateSaved: new Date(),
      savedById: userId
    };

    return await handleAction(
      dtrWithOtService.approve, // You need to add this to dtrWithOtService.js
      audit,
      "Overtime approved and request generated.",
      payload
    );
  };

  const handleFilter = useCallback((value) => {
    if (!value || value.trim() === "") {
      setLogs(filterData);
      return;
    }
    const lowercaseValue = value.toLowerCase();
    const filtered = filterData.filter((f) => {
      const fullName = f?.employeePersonalInformationFullName?.toLowerCase() || "";
      const empId = f?.employeePersonalInformationEmployeeId?.toString().toLowerCase() || "";
      return fullName.includes(lowercaseValue) || empId.includes(lowercaseValue);
    });
    setLogs(filtered);
  }, [filterData]
  );

  return {
    loading,
    logs,
    handleFilter,
    upsertOTOverride,
    approveOT,
    refresh: fetchLogs
  };
};